import { urlPath } from './utils-url-path.js';

class ProductVariantSelect extends HTMLElement {
  abortController;

  /**
   * Connected callback.
   */
  connectedCallback() {
    this.initOptions();
  }

  /**
   * Disconnected callback.
   */
  disconnectedCallback() {
    this.abortController = null;
    this.removeEventListener('change', this.changeHandler);
    this.removeEventListener('keydown', this.keydownHandler);
  }

  /**
   * Initialize the options.
   */
  initOptions() {
    this.options = this.querySelectorAll('[data-variant-id]');
    this.productUrl = this.dataset.productUrl;
    this.sectionId = this.dataset.sectionId;
    this.variantsJson = JSON.parse(this.querySelector('[data-variants-json]').textContent);

    this.changeHandler = (event) => {
      const target = event.target.closest('[data-variant-id]');
      if (target && !this.abortController) this.onVariantChange({ target });
    };

    this.addEventListener('change', this.changeHandler);

    this.keydownHandler = (event) => {
      if (event.key !== 'Enter') return;
      const target = event.target.closest('[data-variant-id]');
      if (target && !this.abortController) {
        event.preventDefault();
        target.checked = true;
        this.onVariantChange({ target });
      }
    };

    this.addEventListener('keydown', this.keydownHandler);

    // Init options based on the URL params
    this.init();
  }

  /**
   * Check if we should switch to a new variant because there are hidden options
   */
  init() {
    this.querySelectorAll('fieldset').forEach((fieldset) => {
      const type = fieldset.dataset.type;
      // Skip if this type appears twice on the page
      const typeCount = document.querySelectorAll(`fieldset[data-type="${type}"]`).length;
      if (typeCount > 1) return;
      fieldset.querySelectorAll('[data-variant-id]').forEach((option) => {
        const checkedOption = fieldset.querySelector('[data-variant-id]:checked');
        // console.log(checkedOption);
        if ((!checkedOption || checkedOption.dataset?.hidden) && !option.dataset?.hidden) {
          option.checked = true;

          // Update the variant because it's should not be selected
          this.onVariantChange({ target: option }, false);
          return false;
        }
      });
    });
  }

  /**
   * Get the variant ID
   * @param {Event} event - The event object.
   */
  getVariantId({ target }) {
    // If the target has a data-variant-id, return it
    const variantId = target.dataset.variantId;
    if (variantId) {
      return variantId;
    }

    // Return if there is no variantJson
    if (!this.variantsJson) return;

    // Get the options that are checked
    const options = [...this.options]
      .filter((option) => option.checked)
      .map((option) => ({
        updated: option.value === target.value,
        option: option.name,
        value: option.value,
      }));

    // First match the updated option from the target
    let variants = this.variantsJson.filter((variant) => variant.options.includes(target.value));

    // Then if we have multiple variants, filter further using the first other option
    if (variants.length > 1) {
      const variants2 = variants.filter((variant) => {
        const value = options.find((option) => !option.updated)?.value;
        return variant.options.includes(value);
      });
      variants = variants2.length === 0 ? variants : variants2;
    }

    // Then if we have multiple variants, filter further using the last other option
    if (variants.length > 1) {
      const variants3 = variants.filter((variant) => {
        const value = options.findLast((option) => !option.updated)?.value;
        return variant.options.includes(value);
      });
      variants = variants3.length === 0 ? variants : variants3;
    }

    return variants[0].id;
  }

  /**
   * Handle variant change.
   * @param {Event} event - The event object.
   */
  async onVariantChange(event, focus = true) {
    const target = event.target;
    if (this.abortController) return;
    this.abortController = new AbortController();

    // Show the loader
    document.querySelector('[data-product-options-loader]').classList.add('active');

    const variantId = this.getVariantId(event);

    const params = this.createParams(variantId);

    // Immediately uncheck all other checkboxes within the same fieldset to improve perceived speed.
    this.updateFieldset(target);

    try {
      const fetchUrl = `${this.productUrl}?${params}`;
      const response = await fetch(fetchUrl, {
        signal: this.abortController.signal,
      });

      const text = await response.text();
      const parsedHTML = new DOMParser().parseFromString(text, 'text/html');

      publish(`variant:changed:${this.sectionId}`, {
        variantId,
        html: parsedHTML,
      });

      this.updateURL(variantId);
    } catch (error) {
      if (error.name === 'AbortError')
        return console.error('Variant change request was aborted due to a new variant selection.');

      console.error(error.message);
      window.toast.error(window.toast_translations.product.variant_error);
    } finally {
      this.abortController = null;
      if (focus) document.querySelector(`#${target.id}`).focus();
      document.querySelector('[data-product-options-loader]').classList.remove('active');
    }
  }

  /**
   * Update URL
   * @param {String} value - URL value
   */
  updateURL(value) {
    const { searchParams } = new URL(window.location);
    searchParams.set('variant', value);

    // Get the current path
    const path = urlPath();

    // Replace the current history state with the new search params, without reloading the page
    try {
      history.replaceState(null, '', `${path}?${searchParams}`);
    } catch (error) {
      console.error('Error updating URL', error);
    }
  }

  /**
   * Create the params.
   * @param {String} variantId - The variant ID.
   * @returns {String} The params.
   */
  createParams(variantId) {
    return [`section_id=${this.sectionId}`, `variant=${variantId}`].join('&');
  }

  /**
   * Update the fieldset.
   * @param {HTMLElement} target - The target element.
   */
  updateFieldset(target) {
    const fieldset = target.closest('fieldset');
    if (!fieldset) return;

    fieldset.querySelectorAll('[data-variant-id]').forEach((option) => {
      option.checked = option === target;
    });
  }
}

customElements.define('product-variant-select', ProductVariantSelect);
